import firebase from 'firebase/compat/app'

import { DataCollectionRecord } from '../services/cms'
import { DataObject, Property, ValueType } from './index'

const isTimestamp = (x: unknown): x is firebase.firestore.Timestamp =>
  typeof x === 'object' && x !== null && 'toDate' in x

const getTimestampProperties = (properties: Property[]) => {
  const singleDateProperties = properties.filter(el => el.type === ValueType.date)

  const singleDateTimeProperties = properties.filter(el => el.type === ValueType.dateTime)

  const multipleDateProperties = properties.filter(
    el => el.type === ValueType.array && el.accept && el.accept === ValueType.date
  )

  const multipleDateTimeProperties = properties.filter(
    el => el.type === ValueType.array && el.accept && el.accept === ValueType.dateTime
  )

  return {
    singleDateProperties,
    singleDateTimeProperties,
    multipleDateProperties,
    multipleDateTimeProperties,
  }
}

const getRefProperties = (properties: Property[]) => {
  const singleAssetProperties = properties.filter(el =>
    [ValueType.image, ValueType.video, ValueType.audio, ValueType.file].includes(el.type)
  )

  const multipleAssetProperties = properties.filter(
    el =>
      el.type === ValueType.array &&
      el.accept &&
      [ValueType.image, ValueType.video, ValueType.audio, ValueType.file].includes(el.accept)
  )

  const singleKeyValueMapProperties = properties.filter(el => el.type === ValueType.keyValueMap)

  const multipleKeyValueMapProperties = properties.filter(
    el => el.type === ValueType.array && el.accept === ValueType.keyValueMap
  )

  return {
    singleAssetProperties,
    multipleAssetProperties,

    singleKeyValueMapProperties,
    multipleKeyValueMapProperties,
  }
}

const timestampToDate = (
  record: DataCollectionRecord,
  properties: Property[],
  assetsProperties: Property[],
  dataObjects: DataObject[]
) => {
  const { singleDateProperties, singleDateTimeProperties, multipleDateProperties, multipleDateTimeProperties } =
    getTimestampProperties(properties)

  singleDateProperties.forEach(({ name }) => {
    if (isTimestamp(record[name])) {
      record[name] = record[name].toDate().toISOString().slice(0, 10)
    }
  })

  singleDateTimeProperties.forEach(({ name }) => {
    if (isTimestamp(record[name])) {
      record[name] = record[name].toDate().toISOString()
    }
  })

  multipleDateProperties.forEach(({ name }) => {
    if (record[name]) {
      record[name].forEach((el: any) => {
        if (isTimestamp(el)) {
          el = el.toDate().toISOString().slice(0, 10)
        }
      })
    }
  })

  multipleDateTimeProperties.forEach(({ name }) => {
    if (record[name]) {
      record[name].forEach((el: any) => {
        if (isTimestamp(el)) {
          el = el.toDate().toISOString()
        }
      })
    }
  })

  const { singleAssetProperties, multipleAssetProperties, singleKeyValueMapProperties, multipleKeyValueMapProperties } =
    getRefProperties(properties)

  singleAssetProperties.forEach(({ name }) => {
    if (record[name]) {
      timestampToDate(record[name], assetsProperties, assetsProperties, dataObjects)
    }
  })

  multipleAssetProperties.forEach(({ name }) => {
    if (record[name]) {
      record[name].forEach((el: any) => {
        timestampToDate(el, assetsProperties, assetsProperties, dataObjects)
      })
    }
  })

  singleKeyValueMapProperties.forEach(({ name, dataObject }) => {
    if (record[name]) {
      const dataObjectProperties = dataObjects.find(({ name }) => name === dataObject)?.properties || []
      timestampToDate(record[name], dataObjectProperties, assetsProperties, dataObjects)
    }
  })

  multipleKeyValueMapProperties.forEach(({ name, dataObject }) => {
    if (record[name]) {
      const dataObjectProperties = dataObjects.find(({ name }) => name === dataObject)?.properties || []
      record[name].forEach((el: any) => {
        timestampToDate(el, dataObjectProperties, assetsProperties, dataObjects)
      })
    }
  })

  return record
}

const dateToTimestamp = (
  record: DataCollectionRecord,
  properties: Property[],
  assetsProperties: Property[],
  dataObjects: DataObject[]
) => {
  const { singleDateTimeProperties, multipleDateTimeProperties } = getTimestampProperties(properties)

  singleDateTimeProperties.forEach(({ name }) => {
    if (record[name]) {
      record[name] = firebase.firestore.Timestamp.fromDate(new Date(record[name]))
    }
  })

  multipleDateTimeProperties.forEach(({ name }) => {
    if (record[name]) {
      record[name].forEach((el: any) => {
        el = firebase.firestore.Timestamp.fromDate(new Date(el))
      })
    }
  })

  const { singleAssetProperties, multipleAssetProperties, singleKeyValueMapProperties, multipleKeyValueMapProperties } =
    getRefProperties(properties)

  singleAssetProperties.forEach(({ name }) => {
    if (record[name]) {
      delete record[name].recordStatus
      dateToTimestamp(record[name], assetsProperties, assetsProperties, dataObjects)
    }
  })

  multipleAssetProperties.forEach(({ name }) => {
    if (record[name]) {
      record[name].forEach((el: any) => {
        delete el.recordStatus
        dateToTimestamp(el, assetsProperties, assetsProperties, dataObjects)
      })
    }
  })

  singleKeyValueMapProperties.forEach(({ name, dataObject }) => {
    if (record[name]) {
      const dataObjectProperties = dataObjects.find(({ name }) => name === dataObject)?.properties || []
      dateToTimestamp(record[name], dataObjectProperties, assetsProperties, dataObjects)
    }
  })

  multipleKeyValueMapProperties.forEach(({ name, dataObject }) => {
    if (record[name]) {
      const dataObjectProperties = dataObjects.find(({ name }) => name === dataObject)?.properties || []
      record[name].forEach((el: any) => {
        dateToTimestamp(el, dataObjectProperties, assetsProperties, dataObjects)
      })
    }
  })
}

export const reconvertRecord = (
  record: DataCollectionRecord,
  properties: Property[],
  assetsProperties: Property[],
  dataObjects: DataObject[]
) => {
  delete record.id
  dateToTimestamp(record, properties, assetsProperties, dataObjects)
}

export const convertRecord = (
  docSnapshot: firebase.firestore.DocumentSnapshot<firebase.firestore.DocumentData>,
  properties: Property[],
  assetsProperties: Property[],
  dataObjects: DataObject[]
) =>
  docSnapshot.exists
    ? timestampToDate({ ...docSnapshot.data(), id: docSnapshot.id }, properties, assetsProperties, dataObjects)
    : {}
